import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box as MuiBox,
  Button as MuiButton,
  Grid as MuiGrid,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  FormatBold as MuiFormatBoldIcon,
  FormatUnderlined as MuiFormatUnderlinedIcon,
  FormatItalic as MuiFormatItalicIcon,
  StrikethroughS as MuiStrikethroughSIcon,
  FormatListBulleted as MuiFormatListBulletedIcon,
  FormatListNumbered as MuiFormatListNumberedIcon,
  InsertLink as MuiInsertLinkIcon,
} from "@mui/icons-material";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import { buttonStyle, templatesConfigStyles } from "./TemplatesConfigStyles";
import { useAlerts } from "common";

const extensions = [
  StarterKit,
  Underline,
  Link.configure({
    openOnClick: true,
    autolink: true,
    defaultProtocol: "https",
    protocols: ["http", "https"],
  }),
];

const RichTextEditor = ({
  setOpenDialog,
  tokenIndex,
  perTemp,
  selectedTempIndex,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();

  const [isRichTextEditorDirty, setIsRichTextEditorDirty] = useState(false);
  const [openASConfirmation, setOpenASConfirmation] = useState(false);

  const content = perTemp.value;

  const editor = useEditor({
    extensions,
    autofocus: false,
    content,
    shouldRerenderOnTransaction: true,

    editorProps: {
      attributes: {
        style: "outline: none;",
      },
    },
    onUpdate: () => {
      setIsRichTextEditorDirty(true);
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    try {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } catch (err) {
      setAlert("error", err.message);
    }
  }, [editor, setAlert]);

  if (!editor) return null;

  const editorValue = editor.getHTML();

  const strippedContent = () => {
    return (
      editorValue
        .replace(/<p>\s*<\/p>/g, "")
        .replace(/<br\s*\/?>/g, "")
        .replace(/<\/?[^>]+(>|$)/g, "")
        .replace(/[\n\r]/g, "")
        .trim() === ""
    );
  };

  const closeCancelASConfirmation = (cancelChoice) => {
    setOpenASConfirmation(false);

    if (cancelChoice) {
      setFieldValue(
        `studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`,
        content
      );
      setIsRichTextEditorDirty(false);
      setOpenDialog(false);
    }
  };

  return (
    <MuiGrid
      container
      sx={{
        border: "1px solid black",
        padding: "10px",
      }}
    >
      <MuiGrid>
        <MuiGrid sx={{ padding: "10px" }}>
          <ConfirmationDialog
            open={openASConfirmation}
            onClose={closeCancelASConfirmation}
            message={t("OutreachDetails.confirmationMessageCancel")}
            okLabel={t("common.yesLabel")}
            cancelLabel={t("common.noLabel")}
          />

          {/* Toolbar Buttons */}

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.bold")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("bold") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
            >
              <MuiFormatBoldIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.underline")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("underline") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <MuiFormatUnderlinedIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.italic")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("italic") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
            >
              <MuiFormatItalicIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.strike")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("strike") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={!editor.can().chain().focus().toggleStrike().run()}
            >
              <MuiStrikethroughSIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.paragraph")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("paragraph") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().setParagraph().run()}
            >
              Para
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.insertLink")} arrow>
            <MuiButton
              sx={buttonStyle}
              onClick={setLink}
              variant={editor.isActive("link") ? "contained" : "outlined"}
              disabled={!editor.can().chain().focus().extendMarkRange("link").run()}
            >
              <MuiInsertLinkIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          {[1, 2, 3].map((level) => (
            <MuiTooltip
              key={level}
              title={t(`templateConfigTable.richTextEditorToolTip.headingH${level}`)}
              arrow
            >
              <MuiButton
                sx={buttonStyle}
                variant={
                  editor.isActive("heading", { level })
                    ? "contained"
                    : "outlined"
                }
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level }).run()
                }
              >
                {`H${level}`}
              </MuiButton>
            </MuiTooltip>
          ))}

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.bulletedList")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("bulletList") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <MuiFormatListBulletedIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>

          <MuiTooltip title={t("templateConfigTable.richTextEditorToolTip.numberedList")} arrow>
            <MuiButton
              sx={buttonStyle}
              variant={editor.isActive("orderedList") ? "contained" : "outlined"}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <MuiFormatListNumberedIcon fontSize="small" />
            </MuiButton>
          </MuiTooltip>
        </MuiGrid>

        <MuiBox sx={templatesConfigStyles.editor}>
          <EditorContent editor={editor} />
        </MuiBox>
      </MuiGrid>

      {/* Footer Buttons */}
      <MuiGrid size={12} container spacing={3} justifyContent="center">
        <MuiGrid>
          <MuiButton
            variant="outlined"
            onClick={() => {
              if (!isRichTextEditorDirty) {
                setOpenDialog(false);
              } else {
                setOpenASConfirmation(true);
              }
            }}
          >
            {t(`formLabel.buttonCancel`)}
          </MuiButton>
        </MuiGrid>

        <MuiGrid>
          <MuiButton
            variant="contained"
            color="primary"
            disabled={!isRichTextEditorDirty || strippedContent()}
            onClick={() => {
              setFieldValue(
                `studyTemplates.[${selectedTempIndex}].getStudyTemp`,
                true
              );

              setFieldValue(
                `studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`,
                editorValue
              );

              setOpenDialog(false);
            }}
          >
            {t("formLabel.buttonOk")}
          </MuiButton>
        </MuiGrid>
      </MuiGrid>
    </MuiGrid>
  );
};

export default RichTextEditor;